import { Link03 } from '@untitledui/icons';

import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';

import type {
  CalendarEventCreatePayload,
  GmailSendPayload,
  ProposedAction,
} from '../core/types';
import {
  isAssociationAddedPayload,
  isAssociationRemovedPayload,
  isCalendarEventCreatePayload,
  isGmailSendPayload,
  isObjectCreatedPayload,
  isObjectDeletedPayload,
  isObjectMergedPayload,
  isPropertyUpdatePayload,
} from '../core/types';

export interface CompletedActionItem {
  icon: React.FunctionComponent<{ className?: string }>;
  label: string;
  sublabel?: string;
  executionOrder: number;
}

/**
 * Statuses that mean the action actually ran and (at least partly) committed —
 * the only actions the success toast should list. Excludes `skipped`
 * (deselected by the user or a deselected dependency) and every error/transient
 * state (`error`, `failure`, `in_flight`, `indeterminate`), plus a null result.
 *
 * NOTE: `partial_success` is intentionally INCLUDED here (an execution event
 * happened), even though actionStatus.tsx's `getActionStatus` classifies it as
 * an error for the per-action status pill. The toast reports what ran, not a
 * quality gate. This divergence is deliberate — do not "unify" the two.
 */
const EXECUTED_TOAST_STATUSES: ReadonlySet<string> = new Set([
  'success',
  'partial_success',
]);

export function wasActionExecuted(action: ProposedAction): boolean {
  const status = action.execution_result?.status;
  return status !== undefined && EXECUTED_TOAST_STATUSES.has(status);
}

/**
 * Derive a display list of completed action items from the proposal's actions.
 * Each item shows an entity type icon + label, matching the screenshot design.
 */
export function getCompletedActionItems(
  actions: ProposedAction[]
): CompletedActionItem[] {
  const executed = actions.filter(wasActionExecuted);

  return executed.map(action => {
    const { payload, enriched_data } = action;

    // CRM object created
    if (isObjectCreatedPayload(payload)) {
      const entityType = enriched_data?.entity_type_name ?? 'ahoy_contact';
      const entityLabel = getTypeLabelByName(entityType);
      return {
        icon: getEntityTypeIconComponent(entityType),
        label: enriched_data?.object_label ?? entityLabel,
        sublabel: `${entityLabel} created`,
        executionOrder: action.execution_order,
      };
    }

    // Property update
    if (isPropertyUpdatePayload(payload)) {
      const entityType = enriched_data?.entity_type_name ?? 'ahoy_contact';
      const entityLabel = getTypeLabelByName(entityType);
      return {
        icon: getEntityTypeIconComponent(entityType),
        label: enriched_data?.object_label ?? entityLabel,
        sublabel: `${entityLabel} updated`,
        executionOrder: action.execution_order,
      };
    }

    // Object deleted
    if (isObjectDeletedPayload(payload)) {
      const entityType = enriched_data?.entity_type_name ?? 'ahoy_contact';
      const entityLabel = getTypeLabelByName(entityType);
      return {
        icon: getEntityTypeIconComponent(entityType),
        label: enriched_data?.object_label ?? entityLabel,
        sublabel: `${entityLabel} deleted`,
        executionOrder: action.execution_order,
      };
    }

    // Object merged
    if (isObjectMergedPayload(payload)) {
      const entityType = enriched_data?.entity_type_name ?? 'ahoy_contact';
      return {
        icon: getEntityTypeIconComponent(entityType),
        label: `${getTypeLabelByName(entityType)} merge`,
        sublabel:
          enriched_data?.source_object && enriched_data?.object_label
            ? `${enriched_data.source_object.label} \u2192 ${enriched_data.object_label}`
            : undefined,
        executionOrder: action.execution_order,
      };
    }

    // Association added
    if (isAssociationAddedPayload(payload)) {
      const sourceType = enriched_data?.entity_type_name ?? '';
      const targetType = enriched_data?.target_object?.entity_type_name ?? '';
      return {
        icon: Link03,
        label: 'Association',
        sublabel:
          sourceType && targetType
            ? `${getTypeLabelByName(sourceType)} \u2192 ${getTypeLabelByName(targetType)}`
            : enriched_data?.object_label,
        executionOrder: action.execution_order,
      };
    }

    // Association removed
    if (isAssociationRemovedPayload(payload)) {
      return {
        icon: Link03,
        label: 'Association removed',
        sublabel: enriched_data?.object_label,
        executionOrder: action.execution_order,
      };
    }

    // Gmail send
    if (isGmailSendPayload(payload)) {
      const params = (payload as GmailSendPayload).action_params;
      const to = params.to?.[0] ?? 'recipient';
      return {
        icon: getEntityTypeIconComponent('ahoy_email_thread'),
        label: 'Email',
        sublabel: `Sent to ${to}`,
        executionOrder: action.execution_order,
      };
    }

    // Calendar event
    if (isCalendarEventCreatePayload(payload)) {
      const params = (payload as CalendarEventCreatePayload).action_params;
      return {
        icon: getEntityTypeIconComponent('ahoy_meeting'),
        label: 'Calendar event',
        sublabel: params.title,
        executionOrder: action.execution_order,
      };
    }

    // Fallback
    return {
      icon: getEntityTypeIconComponent('ahoy_contact'),
      label: 'Action',
      sublabel: 'Completed',
      executionOrder: action.execution_order,
    };
  });
}
